<!-- eslint-disable default-case -->
<script setup lang="ts">
import { useSlots } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { ButtonType, ButtonTheme, ButtonColor } from './index';

export interface Props {
  type?: ButtonType;
  theme?: ButtonTheme;
  color?: ButtonColor,
  isLoading?: boolean;
  isDisabled?: boolean;
  fullWidth?: boolean;
  isIcon?: boolean;
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'button',
  theme: 'primary',
  color: 'green',
  isLoading: false,
  isDisabled: false,
  fullWidth: false,
  isIcon: false,
  ariaLabel: '',
});

const slots = useSlots();

const buttonElement = ref<HTMLElement | null>(null);
let observer: MutationObserver | null = null;

const disableButton = () => {
  if (buttonElement.value && props.isDisabled) {
    buttonElement.value.style.pointerEvents = 'none';
    buttonElement.value.setAttribute('aria-disabled', 'true');
  } else {
    buttonElement.value?.removeAttribute('style');
    buttonElement.value?.setAttribute('aria-disabled', 'false');
  }
};

onMounted(() => {
  if (buttonElement.value) {
    observer = new MutationObserver(() => disableButton());

    observer.observe(buttonElement.value, {
      attributes: true,
      attributeFilter: ['style', 'disabled'],
    });
  }
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});
</script>

<template>
  <button
    ref="buttonElement"
    :type="type"
    :disabled="isDisabled"
    :aria-label="ariaLabel"
    class="rounded-full inline-flex transition-all duration-150 ease-in-out justify-center items-center disabled:opacity-40"
    :class="[
      // green
      {'primary-green-button': theme === 'primary' && color === 'green'},
      {'secondary-green-button': theme === 'secondary' && color === 'green'},
      {'secondary-outline-green-button': theme === 'secondary-outline' && color === 'green'},
      {'!p-0 bg-transparent text-green-900 underline': theme === 'green-underline'},
      // blue
      {'primary-blue-button': theme === 'primary' && color === 'blue'},
      {'secondary-blue-button': theme === 'secondary' && color === 'blue'},
      {'secondary-outline-blue-button': theme === 'secondary-outline' && color === 'blue'},
      // red
      {'primary-red-button': theme === 'primary' && color === 'red'},
      {'secondary-red-button': theme === 'secondary' && color === 'red'},
      {'secondary-outline-red-button': theme === 'secondary-outline' && color === 'red'},
      // other
      {'!p-0 bg-transparent text-blue-900 hover:underline': theme === 'tertiary'},
      {'!p-0 bg-transparent text-blue-900 underline': theme === 'tertiary-underline'},
      {'pointer-events-none': isDisabled},
      isIcon ? 'aspect-square size-10': 'px-12 py-3 text-intro',
      fullWidth ? 'w-full' : 'w-fit',
    ]"
  >
    <span v-if="isLoading" aria-busy class="relative mx-2 animate-spin">
      <FontAwesomeIcon :icon="['fas', 'spinner']" size="lg" />
    </span>

    <span v-else-if="slots['icon-start']" class="relative mr-2">
      <slot name="icon-start" />
    </span>

    <div v-if="!isLoading" class="relative">
      <slot />
    </div>

    <span v-if="slots['icon-end']" class="relative ml-2">
      <slot name="icon-end" />
    </span>
  </button>
</template>
